/** @format */

import React from 'react';
import {
  TableBuildHead,
  TableAddRow,
  TableDeleteRows,
  TableWatchShowLogic,
  TableBuildColumnOptions,
  TableBuildRows,
  refreshColumnsOptions,
  TableGetChildren,
  getEmptyRow,
  TableBuildEmptyRows,
} from './TableFactoryHelpers';

/* Table Controls used from ../controls */
import EntityTable from '../controls/EntityTable';
import EntityWPHorizontalTable from '../controls/EntityTable/EntityWPHorizontalTable';
import EntityWPVerticalTable from '../controls/EntityTable/EntityWPVerticalTable';
import HorizontalTable from '../controls/HorizontalTable';
import HTComboTable from '../controls/HTComboTable';

/*
  tablehdr -> tableRow 1:1 relationship

  Table {
    tableHdr: [],
    tableRow: []
  }

  Different types of tables: HTCombo, Horizontal, Entity

*/

function TableFactory(data, parserFn, currentElements, elementActions) {
  if ((data && data.tableHdr === undefined) || (data && data.tableHdr && data.tableHdr.length === 0)) {
    console.error(data);
    throw Error('TableFactory: missing or empty `tableHdr` property');
  }

  if (data.tableRow === undefined) {
    throw Error('TableFactory: missing `tableRow` property');
  }

  let srcRows = data.tableRow === null ? [] : data.tableRow;

  /*
   * Bridge functions between Parser and Table model
   */

  let updateModel = () => {};
  let updateColumn = () => {};
  let setTableModel = () => {};
  let rowId = data.maxRowInstanceID ? data.maxRowInstanceID : 1;

  const rowKeys = data.tableRow ? Object.keys(data.tableRow) : null;
  if (rowKeys && rowKeys.length) {
    rowId = rowKeys[rowKeys.length - 1];
  }

  let cancelSelectedRows = () => {};
  const rowsPerPage = 5;

  const setUpdateModelFn = fn => (updateModel = fn);
  const getUpdateModelFn = (origRowIndex, colIndex) => newValue => updateModel(origRowIndex, colIndex, true, false, newValue);
  const setRowId = () => ++rowId;

  const setUpdateColumnFn = fn => (updateColumn = fn);
  const getUpdateColumnFn = colIndex => (property, newValue) => updateColumn(colIndex, property, newValue);

  const setTableModelFn = fn => (setTableModel = fn);
  const getSetTableModelFn = () => setTableModel;

  const setCancelSelectedRowsFn = fn => (cancelSelectedRows = fn);
  const getCancelSelectedRowsFn = () => cancelSelectedRows;

  const tableActions = {
    getUpdateModelFn,
    getUpdateColumnFn,
    getSetTableModelFn,
    getRefreshTableFn: refreshTable,
    setRowId,
    getTableProps: () => data,
    onUpdateState: TableWatchShowLogic(getUpdateColumnFn),
  };

  function refreshTable() {
    const { getParserState } = elementActions();
    const { elements, elementsIndex } = getParserState();

    const { tableElements } = TableGetChildren(tableHeadElements, elementActions);

    const elPosition = elementsIndex[data.elementName];
    const elNode = elements[elPosition];

    const oldColumns = TableBuildColumnOptions(tableHeadElements, elementActions, tableActions, data.controlTypeName);
    const newColumns = refreshColumnsOptions(tableElements, oldColumns);

    // refresh the table to reflect the new rows
    elNode.updateComponent({ columns: newColumns });
  }
  const { tableHeadElements, tableHeadOrder } = TableBuildHead(data.elementName, data.tableHdr, parserFn);
  //const { rowsElements, rowsValues } = TableBuildRows(srcRows, tableHeadElements, elementActions, tableActions);

  let rowsElements, rowsValues, rowElementsVsValue;

  if (srcRows.length === 0) {
    srcRows = getEmptyRow(data.tableHdr, data.controlTypeName);
    data.tableRow = srcRows;
    rowElementsVsValue = TableBuildEmptyRows(srcRows, tableHeadElements, elementActions, tableActions, rowId);
    rowsElements = rowElementsVsValue.rowsElements;
    rowsValues = rowElementsVsValue.rowsValues;
  } else {
    rowElementsVsValue = TableBuildRows(srcRows, tableHeadElements, elementActions, tableActions);
    rowsElements = rowElementsVsValue.rowsElements;
    rowsValues = rowElementsVsValue.rowsValues;
  }

  rowsElements.map(ele => {
    ele.props.parentTableType = data.controlTypeName;
    ele.props.hasParentTable = data.controlTypeName !== undefined;
    return ele;
  });
  const addNewRowFn = (defaultValues = undefined, emptyColume = false, controlTypeName = '') => {
    TableAddRow(defaultValues, tableHeadElements, tableHeadOrder, elementActions, tableActions, emptyColume, false, controlTypeName);
  };

  const deleteRowsFn = (selectedRows, tableType) => {
    TableDeleteRows(selectedRows, tableHeadElements, tableHeadOrder, elementActions, tableActions, tableType);
    const cancelRowFn = getCancelSelectedRowsFn();
    cancelRowFn();
  };

  const columns = TableBuildColumnOptions(tableHeadElements, elementActions, tableActions, data.controlTypeName);

  const tableType = data.controlTypeName;
  let TableComponent = false;

  switch (tableType) {
    case 'Horizontal':
      TableComponent = HorizontalTable;
      break;
    case 'WPHorizontalTable':
      TableComponent = EntityWPHorizontalTable;
      break;
    case 'WPVerticalTable':
      TableComponent = EntityWPVerticalTable;
      break;
    case 'HTCombo':
      TableComponent = HTComboTable;
      break;
    case 'Entity':
      TableComponent = EntityTable;
      break;
    default:
      throw Error(`TableFactory: Unknown table type ${data.controlTypeName}`);
  }

  const FinalComponent = props => {
    const pagination = data.pagination ? data.pagination : false;
    return (
      <TableComponent
        rows={rowsValues}
        columns={columns}
        selectableRows={true}
        tableHeadElements={tableHeadElements}
        tableBodyElements={rowsElements}
        elementActions={elementActions}
        addNewRowFn={addNewRowFn}
        refreshTableFn={tableActions.getRefreshTableFn}
        deleteRowsFn={deleteRowsFn}
        cancelRowsFn={getCancelSelectedRowsFn}
        setUpdateModelFn={setUpdateModelFn}
        setUpdateColumnFn={setUpdateColumnFn}
        setTableModelFn={setTableModelFn}
        setCancelSelectedRows={setCancelSelectedRowsFn}
        rowsPerPage={rowsPerPage}
        tableActions={tableActions}
        tableHeadOrder={tableHeadOrder}
        pagination={pagination}
        {...props}
      />
    );
  };

  return {
    elements: rowsElements,
    component: FinalComponent,
    onInitialization: () => refreshTable(),
  };
}

export default TableFactory;



// WEBPACK FOOTER //
// ./src/ui/library/ContentParser/elements/TableFactory.js